// Static event-type -> display-title map plus its fallback humanizer,
// split out of event-config.tsx to keep that file under the size ceiling.
export const EVENT_TYPE_TITLES: Record<string, string> = {
    patient_created: 'Patient created',
    case_submitted: 'Case submitted',
    case_approved: 'Case approved',
    case_declined: 'Case declined',
    appointment_scheduled: 'Appointment scheduled',
    monitoring_started: 'Monitoring started',
    monitoring_state_changed: 'Monitoring updated',
    dm_comfort_scan_requested: 'Additional scan requested',
    dm_aligner_change_forced: 'Next aligner change forced',
    dm_forced_aligner_change_cancelled: 'Forced aligner change cancelled',
    rdi_submission: 'RDI submission',
    sdi_submission: 'SDI submission',
    dx_report_submission: 'Dx report submitted',
    followup_request: 'Follow-up sent',
    refinement_trigger: 'Refinement requested',
    refinement_approved: 'Refinement approved',
    refinement_rejected: 'Refinement rejected',
    refinement_records_submitted: 'Refinement records submitted',
    retention_trigger: 'Retention requested',
    retention_approved: 'Retention approved',
    retention_rejected: 'Retention rejected',
    retention_records_submitted: 'Retention records submitted',
    treatment_closed: 'Treatment closed',
    // Automation: manufacturer (Spark) case-status change, written by odo-integrations.
    manufacturer_status_updated: 'Manufacturer status updated',
    // Workflow engine actions - keyed by the engine action id (kebab-case).
    'approve-records': 'Records approved',
    'reject-records': 'Records rejected',
    'records-resubmitted': 'Records re-submitted',
    'create-in-manufacturer': 'Created in manufacturer',
    'submit-design-plan': 'Tx design submitted',
    'design-pre-approved': 'Design pre-approved',
    'plan-received': 'Plan received',
    'approve-design-review': 'Design approved',
    'reject-design-review': 'Design rejected',
    'dx-report-draft-started': 'Dx report draft started',
    'create-dx': 'Dx report created',
    'submit-dx-for-review': 'Dx report submitted for review',
    'approve-dx-review': 'Dx review approved',
    'approve-dx-review-v2': 'Dx review approved',
    'ortho-approve-design': 'Design & Dx approved by the orthodontist',
    'ortho-request-design-changes': 'Orthodontist feedback · back to Submit design & Dx',
    'reject-dx-review': 'Dx review rejected',
    'hand-back-design': 'Handed back to Submit design & Dx',
    'hand-back-dx': 'Handed back to Review design & finalize Dx',
    'design-unchanged': 'Design unchanged',
    'design.final-review.handed-back': 'Final review notes',
    'approve-case': 'Case approved',
    'request-revision': 'Revision requested',
    'decline-treatment': 'Treatment declined',
    'reactivate-case': 'Case reactivated',
    'archive-case': 'Case archived',
    'expire-stale-design': 'Design expired',
    'force-status': 'Status forced',
    'submit-redesign': 'Redesign submitted to manufacturer',
    'redesign-hand-back-design': 'Handed back to Submit design & Dx',
    'redesign-hand-back-dx': 'Handed back to Review design & finalize Dx',
    'redesign-unchanged': 'Design unchanged',
    'approve-redesign-dx-review-v2': 'Redesign sent to orthodontist review',
    'approve-redesign-skip-dx-v2': 'Redesign sent to orthodontist review · Dx unchanged',
    'ortho-approve-redesign': 'Redesign approved by the orthodontist',
    'ortho-request-redesign-changes':
        'Orthodontist feedback on the redesign · back to Submit design & Dx',
    'create-sdi-v2': 'SDI created · sent to orthodontist review',
    'ortho-approve-sdi': 'SDI approved by the orthodontist',
    // No longer an engine action; kept for the events already on the timeline.
    'reject-redesign-dx-review': 'Revised Dx review rejected',
    'clarification-requested': 'Clarification requested',
    'clarification-provided': 'Clarification provided',
    // Ref-1 "Review for refinement" GP decision.
    'review-approve-refinement': 'Refinement approved',
    'refuse-refinement-continue': 'Refinement refused',
    // Ref-0 → Tx-1: treatment resumed after a refused refinement.
    'refinement-resume-treatment': 'Treatment resumed',
    'end-treatment-start-retention': 'Treatment ended · retention',
    'end-treatment-complete': 'Treatment ended',
    // Ref-2 → Ref-2.1: GP submits refinement records.
    'upload-refinement-records': 'Refinement records submitted',
    // Ref-2.1: clinical records review.
    'approve-refinement-records': 'Refinement records approved',
    'reject-refinement-records': 'Refinement records rejected',
    // Ref-3: refinement design submission.
    'submit-refinement-for-design': 'Refinement design submitted',
    'refinement-design-pre-approved': 'Refinement design pre-approved',
    // Ref-4 / Ref-4.1: manufacturer returns the design.
    'refinement-design-received': 'Refinement design received',
    // Ref-5: clinical design review.
    'approve-refinement-design-review': 'Refinement design approved',
    'reject-refinement-design-review': 'Refinement design rejected',
    // Ref-6: RDI created (the RDI document itself is an rdi_submission event).
    'create-refinement-rdi': 'Refinement RDI created',
    // Ref-6 -> Ref-7 direct (practice lacks the refinement-design-approval flag).
    'create-refinement-rdi-skip-approval': 'Refinement RDI created',
    // Ref-6.1: the GP (V1) or the orthodontist (V2) reviews the RDI.
    'approve-refinement-design': 'Refinement approved for manufacturing',
    'request-refinement-design-revision': 'Refinement design revision requested',
    // Ref-7: case submitted to the manufacturer.
    'submit-refinement-for-manufacturing': 'Refinement submitted for manufacturing',
    // Ref-8: manufacturer shipped the refinement aligners.
    'refinement-shipped': 'Refinement aligners shipped',
    // Ret-1 "Ready for retention" GP decision.
    'approve-retention': 'Retention approved',
    'refuse-retention-continue': 'Retention refused',
    'refuse-retention-end': 'Retention refused',
    'submit-retention-records': 'Retention records submitted',
    // Ret-2.1 / Ret-3 / Ret-4 / Ret-5 admin + GP steps.
    'approve-retention-records': 'Retention records approved',
    'reject-retention-records': 'Retention records rejected',
    'submit-retention-to-manufacturer': 'Retention submitted for manufacturing',
    'retainers-shipped': 'Retainers shipped',
    'deliver-retainers': 'Retainers delivered',
    // Tx-2 / Tx-3: automation moves the case in and out of the VA steps.
    'dm-va-due': 'DM VA due',
    'clinical-va-due': 'Clinical VA due',
    'clinical-vas-resolved': 'Clinical VAs resolved',
    'vas-resolved': 'VAs resolved',
    virtual_appointment_triggered: 'VA triggered',
    'enter-hold': 'Placed on hold',
    'set-priority': 'Priority changed',
    admin_patient_updated: 'Patient updated',
    admin_patient_moved_practice: 'Practice changed',
    orthopulse_ordered: 'OrthoPulse ordered'
};

export function humanizeType(type: string): string {
    const spaced = type.replace(/[_-]/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
